// Normalises Supabase/PostgREST/Auth/Edge errors into { code, message, retryable }.
// Server functions raise stable codes (e.g. "rank_exceeded"); they are mapped to friendly copy here.

export const ERROR_MESSAGES = {
  not_authenticated: 'Your session has ended. Please sign in again.',
  permission_denied: 'You don’t have permission to do that.',
  mfa_required: 'This action needs a verified authenticator code. Verify your second factor and try again.',
  rank_exceeded: 'You can only manage people and roles below your own level.',
  permission_exceeded: 'That would give someone access you don’t hold yourself.',
  cannot_modify_self: 'Another administrator must change your own role, status or permissions.',
  cannot_modify_own_role: 'You can’t change the permissions of your own role.',
  last_super_admin: 'Assign another Super Admin before changing this one.',
  member_type_change_not_allowed: 'Staff, client and developer accounts use separate invitation flows.',
  reason_required: 'Please give a reason (at least 3 characters).',
  invalid_role: 'That role does not exist.',
  invalid_status: 'That status is not allowed.',
  invalid_permission: 'That permission does not exist.',
  scope_required: 'Choose a scope for this permission.',
  scope_not_allowed: 'That scope is not available for this permission.',
  client_scope_only: 'Client users can only hold client-scoped permissions.',
  permission_restricted_for_role: 'This role is not allowed to hold that permission.',
  super_admin_role_locked: 'The Super Admin role always holds every permission.',
  expiry_in_past: 'The expiry date must be in the future.',
  invalid_email: 'Enter a valid email address.',
  already_member: 'This person is already a member of the organization.',
  invitation_pending: 'An invitation is already pending for this email. Resend or revoke it instead.',
  invitation_not_pending: 'Only pending invitations can be revoked.',
  invitation_not_resendable: 'This invitation can no longer be resent.',
  client_invitations_not_available: 'Client portal invitations arrive with the client portal (Phase 5).',
  invitation_invalid: 'This invitation link is not valid. Check that you copied the full link.',
  invitation_expired: 'This invitation has expired. Ask your administrator to resend it.',
  invitation_revoked: 'This invitation was revoked by an administrator.',
  invitation_accepted: 'This invitation has already been used. Sign in instead.',
  invitation_email_mismatch: 'You’re signed in with a different email than the one invited.',
  account_exists: 'An account already exists for this email. Sign in to accept the invitation.',
  password_length: 'Use 12–128 characters.',
  password_complexity: 'Include upper- and lower-case letters and a number.',
  unknown_currency: 'One of the selected currencies is not supported.',
  invalid_timezone: 'Choose a valid timezone.',
  not_found: 'That record no longer exists.',
  server_misconfigured: 'The server is not fully configured yet. Contact your administrator.',
  network: 'Network problem — check your connection and try again.',
  invalid_credentials: 'Email or password is incorrect.',
  email_not_confirmed: 'Confirm your email address before signing in.',
  rate_limited: 'Too many attempts. Wait a minute and try again.',
  invalid_totp: 'That code didn’t work. Check your authenticator app and try again.',
  unknown: 'Something went wrong. Please try again.',
};

const KNOWN_CODES = Object.keys(ERROR_MESSAGES);

export function toAppError(error) {
  if (!error) return { code: 'unknown', message: ERROR_MESSAGES.unknown, retryable: true };
  if (error.code && ERROR_MESSAGES[error.code] && error.message === ERROR_MESSAGES[error.code]) return error;

  const msg = String(error.message ?? error.error_description ?? error ?? '');
  const lower = msg.toLowerCase();

  if (error.name === 'TypeError' && lower.includes('fetch')) return mk('network', true);
  if (lower.includes('failed to fetch') || lower.includes('networkerror')) return mk('network', true);
  if (lower.includes('invalid login credentials')) return mk('invalid_credentials');
  if (lower.includes('email not confirmed')) return mk('email_not_confirmed');
  if (error.status === 429 || lower.includes('rate limit') || lower.includes('too many')) return mk('rate_limited', true);
  if (lower.includes('invalid totp') || (lower.includes('totp') && lower.includes('invalid'))) return mk('invalid_totp');
  if (lower.includes('jwt expired') || lower.includes('refresh token')) return mk('not_authenticated');
  if (lower.startsWith('permission denied for')) return mk('permission_denied');

  const exact = KNOWN_CODES.find((code) => msg === code || error.code === code);
  if (exact) return mk(exact);
  const embedded = KNOWN_CODES.find((code) => new RegExp(`\\b${code}\\b`).test(msg));
  if (embedded) return mk(embedded);

  return { code: 'unknown', message: ERROR_MESSAGES.unknown, retryable: true, detail: msg };
}

function mk(code, retryable = false) {
  return { code, message: ERROR_MESSAGES[code], retryable };
}

/** Unwrap a Supabase response, throwing a normalised error. */
export function unwrap({ data, error }) {
  if (error) throw toAppError(error);
  return data;
}

/** Unwrap a paginated list response into a stable { data, count } shape. */
export function unwrapList({ data, error, count }) {
  if (error) throw toAppError(error);
  const rows = data ?? [];
  return { data: rows, count: typeof count === 'number' ? count : rows.length };
}
